import React, { FC, useMemo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import EditIcon from 'transfix-icons/build/EditIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { TeamMemberCarrierDisplay } from 'apps/fleet-planner/components/team/TeamMemberCarrierDisplay'
import { TeamMemberContactDisplay } from 'apps/fleet-planner/components/team/TeamMemberContactDisplay'
import { TeamMemberName } from 'apps/fleet-planner/components/team/TeamMemberName'
import 'apps/fleet-planner/scss/pages/_team.scss'
import * as Types from 'apps/fleet-planner/types'
import { TableCell, TableRow } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import TeamMemberSmsOptInDisplay from './TeamMemberSmsOptInDisplay'

interface ITeamMembersRowProps {
  teamMember: Types.ITeamMember
  onEdit: (teamMember: Types.ITeamMember) => void
  isUpdated?: boolean
}

export const TeamMemberTableRow: FC<ITeamMembersRowProps> = ({
  isUpdated = false,
  onEdit,
  teamMember,
}) => {
  const { motorCarrierDetails = [] } = teamMember || {}
  const [hasHighlight, setHasHighlight] = useState<boolean>(false)

  // if isUpdated is toggled, we want to highlight the row but only do it once per mount
  useEffectOnce(() => {
    if (isUpdated && !hasHighlight) {
      setHasHighlight(true)
      const timer = setTimeout(() => setHasHighlight(false), 3000)
      return () => clearTimeout(timer)
    }
    return undefined
  })

  const roles = useMemo(() => {
    const rolesList = motorCarrierDetails.map(mcd =>
      capitalize((mcd.userType || '').replace('_', ' '))
    )
    return [...new Set(rolesList)].sort().join(', ')
  }, [motorCarrierDetails])

  const editTeamMemberClickHandler = () => onEdit(teamMember)

  const fmsTeamMembersShowDriverSmsOptInStatusEnabled = useFeatureFlag(
    'fmsTeamMembersShowDriverSmsOptInStatus'
  )

  return (
    <TableRow
      dataTest='team-member-list-item'
      className={`${hasHighlight ? 'fp-loads-list-row-updated' : ''}`}>
      <TableCell>
        <TeamMemberName teamMember={teamMember} />
      </TableCell>
      <TableCell>{roles}</TableCell>
      <TableCell>
        <TeamMemberCarrierDisplay motorCarriers={motorCarrierDetails} />
      </TableCell>
      {fmsTeamMembersShowDriverSmsOptInStatusEnabled && (
        <TableCell>
          <TeamMemberSmsOptInDisplay teamMember={teamMember} />
        </TableCell>
      )}
      <TableCell>
        <TeamMemberContactDisplay teamMember={teamMember} />
      </TableCell>
      <TableCell>
        {!!teamMember.canEdit && (
          <IconButton
            color='tertiary'
            data-test='team-member-list-item-edit-button'
            label='Edit'
            Icon={EditIcon}
            onClick={editTeamMemberClickHandler}
            size='large'
          />
        )}
      </TableCell>
    </TableRow>
  )
}
